import { useInfiniteQuery } from '@tanstack/react-query';
import {
  dexBotKeys,
  DexBotService,
} from 'api/services/httpServices/DexBotService';
import {
  GetWhitelistedTokensParams,
  WhitelistedTokenData,
} from 'api/types/httpsTypes/dex-bot';
import { ErrorPageDex } from '../../../research/components/shared/Common';
import Table from 'modules/shared-components/data-display/table';
import Header from 'modules/shared-components/data-display/table/header';
import { FC } from 'react';
import { useWhitelistedTokensSearchFilters } from '../usePageParams';
import { desktopColumns, mobileColumns } from './columnsWhitelistedTokens';
import { ResponsiveTableColumn } from 'modules/shared-components/data-display/table/types';
import ErrorBoundary from 'components/error-boundaries';
import { screenGte, useMediaQuery } from 'modules/media/use-media-query';
import { useInView } from 'react-intersection-observer';
import { useSelectedChain } from '../../../components/chains/getChains';

export function thDynamicClass(s: string) {
  return s === 'PAIR' ? 'col-span-2' : '';
}

export function WhitelistedTokensTable() {
  const { filtersState } = useWhitelistedTokensSearchFilters();

  const selectedChain = useSelectedChain();
  const params: Partial<GetWhitelistedTokensParams> = {
    ...(filtersState.whitelistedTokensAddress
      ? { search: filtersState.whitelistedTokensAddress }
      : {}),
    chain: selectedChain,
  };

  const isDesktop = useMediaQuery(screenGte.small);
  const columns = isDesktop ? desktopColumns() : mobileColumns();

  const {
    data,
    error,
    isInitialLoading: isLoading,
    fetchNextPage,
  } = useInfiniteQuery({
    queryFn: ({ pageParam = { limit: 10, offset: 0 } }) => {
      return DexBotService.getWhitelistedTokens({
        ...params,
        withAnalytics: true,
        limit: pageParam.limit,
        offset: pageParam.offset,
      });
    },
    queryKey: dexBotKeys.getWhitelistedTokens(params),
    getNextPageParam: (lastPage) => ({
      offset: lastPage.pagination.offset + lastPage.pagination.limit ?? 0,
      limit: 10,
    }),
    keepPreviousData: true,
  });

  const { ref } = useInView({
    onChange: (inView) => {
      if (inView) {
        void fetchNextPage();
      }
    },
  });

  const items = data?.pages?.flatMap((page) => page.data) || [];
  const pagination = data?.pages?.[0]?.pagination;

  return (
    <div className="h-full">
      {error ? (
        <ErrorPageDex height="h-[500px]" />
      ) : (
        <div>
          <div className="flex flex-col justify-between xxs:min-h-[600px] lg:min-h-[500px] h-full space-y-2">
            <div className="overflow-x-auto">
              <div className="w-full lg:min-w-[1000px] ">
                <Table className="xxs:[&_td:first-of-type:not(.details)]:pl-2 xxs:[&_th:first-of-type]:pl-2 lg:[&_td:first-of-type:not(.details)]:pl-6 lg:[&_th:first-of-type]:pl-6">
                  <Header
                    trClassName="lg:grid lg:grid-cols-9 lg:dark:bg-dex-black-800  border-tailwind xxs:border-x-0 xxs:border-t-0 xxs:border-b-dex-black-700"
                    columns={columns}
                    thDynamicClass={thDynamicClass}
                    rowClassName="xxs:p-1"
                    className="lg:dark:bg-dex-black-700 xxs:text-xs lg:grid"
                  />
                  <tbody className="lg:block lg:h-terminal overflow-y-auto">
                    {isLoading ? (
                      [...Array(10).keys()]
                        .map((i) => i + 1)
                        .map((index) => (
                          <Table.LoadingRow
                            key={index}
                            elementClassName="xxs:dark:bg-dex-black-700"
                            thDynamicClass={thDynamicClass}
                            className="lg:grid lg:grid-cols-9 lg:dark:bg-dex-black-800"
                            columns={columns}
                          />
                        ))
                    ) : (
                      <>
                        {items?.map((item, rowId) => (
                          <Row key={rowId} data={item} columnsDef={columns} />
                        ))}{' '}
                        {pagination && pagination.total > items.length && (
                          <Table.LoadingRow
                            ref={ref}
                            elementClassName="xxs:dark:bg-dex-black-700"
                            className={`lg:grid lg:grid-cols-9 lg:dark:bg-dex-black-800 `}
                            columns={columns}
                          />
                        )}
                      </>
                    )}
                  </tbody>
                </Table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface Props {
  columnsDef: ResponsiveTableColumn<WhitelistedTokenData>[];
  data: WhitelistedTokenData;
}

const Row: FC<Props> = ({ data, columnsDef }) => {
  return (
    <tr className="border-t border-dex-black-700 lg:dark:bg-dex-black-800 cursor-pointer items-center lg:grid lg:grid-cols-9">
      <ErrorBoundary>
        {columnsDef.map((column) => {
          const Column = column.component;
          if (!Column) return;
          return (
            <Table.Td className={thDynamicClass(column.key)} key={column.key}>
              <Column data={data} />
            </Table.Td>
          );
        })}
      </ErrorBoundary>
    </tr>
  );
};
